import type { ReactNode } from 'react'
import { AlertErrorCircle, InfoCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'

/** Dismissible error banner shown above the blank-state card when a first build failed. */
export type AssetEmptyStateError = {
  headline: string
  detail: string
  dismissLabel: string
  onDismiss: () => void
}

interface AssetEmptyStateProps {
  illustration: ReactNode
  title: string
  body: string
  ctaLabel: string
  onCtaClick: () => void
  error?: AssetEmptyStateError
  /** In-progress Add Files session notice shown above the card (e.g. "3 files uploading — continue in Add Files"). */
  notice?: string
}

/**
 * Blank state shown when the matter has no living asset yet (Figma: blank state). When a
 * first-generation attempt failed, the dismissible error banner from Figma "First generation - error"
 * (node 9095:366770) shows above the card — the user lands here after a failed Generate.
 *
 * Presentational: the feature supplies the illustration, copy, CTA, and (optional) error banner.
 */
export default function AssetEmptyState(props: AssetEmptyStateProps) {
  const { illustration, title, body, ctaLabel, onCtaClick, error, notice } = props

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {error && (
        <div className="px-4 pt-4 lg:px-8 lg:pt-6">
          <div className="flex items-start gap-2.5 pl-4 pr-2 py-2.5 bg-red-50 border border-brand-neutral-200 rounded-md">
            {/* Stacks below sm so the detail text keeps full width instead of wrapping in a sliver
                beside the nowrap headline. */}
            <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="flex items-center gap-1 shrink-0">
                <AlertErrorCircle className="w-6 h-6 shrink-0 text-red-800" />
                <span className="text-xs font-medium text-red-800 whitespace-nowrap">{error.headline}</span>
              </span>
              <span className="text-xs font-medium text-brand-neutral-950">{error.detail}</span>
            </div>
            <Button onClick={error.onDismiss} variant="plain" color="secondary" size="xs" className="shrink-0">
              {error.dismissLabel}
            </Button>
          </div>
        </div>
      )}
      {notice && (
        <div className="px-4 pt-4 lg:px-8 lg:pt-6">
          {/* Colour tokens follow InlineMessage's info variant (bg-brand-50 / text-brand-700). */}
          <div className="flex items-center gap-2 pl-4 pr-4 py-2.5 bg-brand-50 border border-brand-neutral-200 rounded-md">
            <InfoCircle className="w-5 h-5 shrink-0 text-brand-700" />
            <span className="text-xs font-medium text-brand-700">{notice}</span>
          </div>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-[930px] bg-white border border-brand-neutral-200 rounded-lg flex flex-col items-center gap-4 px-4 py-6">
          {/* Figma chronology blank state (9061:258421): outlined document + medical cross + clock disc. */}
          {illustration}
          <div className="flex flex-col items-center gap-0.5 text-center w-full max-w-[460px]">
            <p className="text-base font-medium text-brand-neutral-950 leading-6">{title}</p>
            <p className="text-sm font-normal text-brand-neutral-600 tracking-[-0.15px] leading-5">{body}</p>
          </div>
          <Button onClick={onCtaClick} size="sm">
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
